import { Box, Button, Card, CardContent, Grid, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import coreConfig from "../../common/coreConfig";
import CommonUiModel from "../../common/commonUiModel";
import commonValidatedScopeUiModel from "../../common/commonValidatedScope";
import axiosBase from "../../common/axiosBase";
import { useStateContext } from "@durlabh/dframework-ui";
const { eqiupmentConfigMap, colorMapper, apiConfig } = coreConfig;

export default function ValidateScope() {
	const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
	const isEdit = searchParams.get('edit') === 'true';
	const key = isEdit ? "CurrentStagedRevisionId" : "CurrentRevisionId";
	const theme = useTheme();
	const screen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const is1024x768 = useMediaQuery('(max-width: 1024px) and (max-height: 768px)');
	const navigate = useNavigate();
	const { id } = useParams();
	const [equipmentData, setEquipmentData] = useState({}); // State to store fetched data
	const { stateData = {} } = useStateContext();
	const { userDetails = {} } = stateData.getUserData || {};
	const equipmentList = userDetails.equipmentsList || [];
	const isReadOnly = !equipmentList.includes(Number(id));

	const model = useMemo(() => {
		if (Object.keys(equipmentData)?.length) {
			const equipmentConfig = eqiupmentConfigMap[equipmentData.EquipmentId];
			const { scopeApi, columns  = []} = equipmentConfig;
			if (!scopeApi) {
				return navigate("/dashboard");
			}
			const nonOverridenColumns = commonValidatedScopeUiModel.columns.filter(col => !columns.find(c => c.field === col.field));
			return new CommonUiModel({
				...commonValidatedScopeUiModel,
				columns: [
					...nonOverridenColumns,
					...columns
				],
				api: scopeApi
			});
		}
	}, [equipmentData]);

	const fetchData = async () => {
		const response = await axiosBase({
			method: apiConfig.reqMethod.GET,
			api: apiConfig.endpoints.equipment + "/" + id
		});
		setEquipmentData(response.data.data);
	};

	useEffect(() => { fetchData(); }, []);

	const onAdd = () => {
		const keyData = JSON.stringify({ "RevisionId": equipmentData[key] });
		const encodedJson = encodeURIComponent(keyData);
		navigate(`/searchScope/${id}/${equipmentData[key] || 0}?baseData=${encodedJson}`);
	};

	const onCellDoubleClickOverride = (event) => {
		navigate(`/validatescope/${id}/${event.row.ScopeHookupId}`);
	};

	const permissions = useMemo(() => ({ add: isEdit, edit: isEdit, delete: isEdit }), [isEdit]);
	const baseFilters = useMemo(() => [{
		field: "RevisionId",
		operator: "equals",
		value: Number(equipmentData[key] || 0),
		type: "number"
	}], [equipmentData, key]);
	return (
		<Grid container>
			<Grid size={{ xs: 12, md: 12 }} sx={{ marginBottom: '10px', marginTop: "10px" }}>
				<Box display="flex" alignItems="center" justifyContent='space-between' sx={{
					backgroundColor: isEdit ? colorMapper.Draft : colorMapper.Production,
					borderRadius: '5px',
					padding: '10px',
					color: 'text.secondary'
				}}>
					<Typography variant="h5">Validated Scopes</Typography>
					<Typography variant="h5" m="auto">Status: {isEdit ? coreConfig.draft : coreConfig.production}</Typography>
					<Typography variant="h5" m="auto">Revision - {isEdit ? "Staging" : equipmentData.Revision}</Typography>
					<Box>
						<Button variant="contained" onClick={() => navigate("/dashboard")}>Back</Button>
					</Box>
				</Box>
			</Grid>
			<Grid container>
				<Grid size={{ xs: 12 }} height={'fit-content'}>
					<Card>
						<CardContent>
							<Grid container spacing={2} height={'fit-content'}>
								<Card sx={{ backgroundColor: '#d3d0d094', padding: '1em', textWrap: 'nowrap', width: '30%', height: '12.5em', alignSelf: 'center', marginLeft: '4em' }}>
									<Grid size={{ xs: 6, md: is1024x768 ? 6 : 3 }} display="flex" justifyContent="space-between" height={'fit-content'}>
										<Grid
											container
											sx={{
												flexDirection: 'row-reverse',
												padding: '1em',
												'& .keyValueRow': {
													display: 'grid',
													gridTemplateColumns: screen || is1024x768 ? '130px 50px 50px' : '150px 100px 100px',
													'& .keyLabel': { fontWeight: 'bold', width: 'auto' },
													'& .keyValue': { marginLeft: screen || is1024x768 ? '-2em' : '-4em' }
												}
											}}
										>
											<Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
												<Typography variant="body1" className="keyLabel">
													Equipment Model :
												</Typography>
												<Typography variant="body1" component="span">
													{' '}
												</Typography>
												<Typography variant="body1" className="keyValue">
													{equipmentData.ModelName || ""}
												</Typography>
											</Grid>
											<Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
												<Typography variant="body1" className="keyLabel">
													Equipment Type :
												</Typography>
												<Typography variant="body1" component="span">
													{' '}
												</Typography>
												<Typography variant="body1" className="keyValue">
													{' '}
													{equipmentData.EquipmentType || ""}
												</Typography>
											</Grid>
											<Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
												<Typography variant="body1" className="keyLabel">
													Equipment Family :
												</Typography>
												<Typography variant="body1" component="span">
													{' '}
												</Typography>
												<Typography variant="body1" className="keyValue">
													{' '}
													{equipmentData.EquipmentFamily || ""}
												</Typography>
											</Grid>
											<Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
												<Typography variant="body1" className="keyValue">
													{' '}
													<br></br>
												</Typography>
											</Grid>
											<Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
												<Typography variant="body1" className="keyLabel">
													Current Revision :
												</Typography>
												<Typography variant="body1" component="span" />
												<Typography variant="body1" className="keyValue">
													{' '}
													{equipmentData.Revision || ""}
												</Typography>
											</Grid>
											<Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
												<Typography variant="body1" className="keyLabel">
													Current Staging Status :
												</Typography>
												<Typography variant="body1" component="span" marginLeft='1.5rem' />
												<Typography variant="body1" className="keyValue">
													{' '}
													{equipmentData.RevisionStatus || ""}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Card>
								<Grid size={{ xs: 6, md: is1024x768 ? 6 : 2 }} display="flex" justifyContent="space-between">
									<Box>
										<Box
											component="img"
											src={`/${equipmentData.EquipmentTypeImage}`}
											alt="washer"
											sx={{ height: 245, paddingLeft: 10, marginBottom: '1em' }}
										/>
									</Box>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			<Grid size={{ xs: 12 }} mt={2}>
				{model && <model.Grid permissions={permissions} baseFilters={baseFilters} onAddOverride={onAdd} readOnly={isReadOnly} onCellDoubleClickOverride={onCellDoubleClickOverride} />}
			</Grid>
		</Grid>
	);
}